import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { fetchCompanyMemberList, getMemberRoleLabel, type ApiCompanyMember } from "./companyMemberAPI"

export interface TeamMember {
  id: number
  name: string
  avatar: number | string | null
  role: string
  designation: string
  department: string
}

interface CompanyMembersState {
  members: TeamMember[]
  status: "idle" | "loading" | "succeeded" | "failed"
  error: string | null
}

const initialState: CompanyMembersState = {
  members: [],
  status: "idle",
  error: null,
}

// Helper function to generate avatar emoji based on gender
const getAvatarEmoji = (gender: "M" | "F" | null): string => {
  if (gender === "F") return "👩‍💼"
  if (gender === "M") return "👨‍💼"
  return "👤"
}

// Helper function to format full name
const formatFullName = (
  first: string,
  middle: string | null,
  last: string
): string => {
  const parts = [first, middle, last].filter(Boolean)
  return parts.join(" ")
}

// Transform API member to TeamMember format
const transformMemberData = (member: ApiCompanyMember): TeamMember => {
  // Normalize profile_picture to an ID (number) if present, otherwise null
  let profilePictureId: number | null = null
  if (typeof member.profile_picture === "number") {
    profilePictureId = member.profile_picture
  } else if (
    member.profile_picture &&
    typeof member.profile_picture === "object" &&
    "id" in member.profile_picture
  ) {
    profilePictureId = (member.profile_picture as { id: number }).id
  }

  const groups: string[] =
    Array.isArray(member.groups) && member.groups.length > 0
      ? member.groups
      : member.group
      ? [member.group]
      : []

  const roleLabel =
    groups.length > 0
      ? groups.map((g) => getMemberRoleLabel(g)).join(", ")
      : ""

  return {
    id: member.id,
    name: formatFullName(member.first_name, member.middle_name, member.last_name),
    // Use numeric profile picture ID if available, otherwise fallback to emoji
    avatar: profilePictureId ?? getAvatarEmoji(member.gender),
    role: roleLabel,
    designation: member.designation || "",
    department: member.department?.name || "",
  }
}

export const fetchCompanyMembers = createAsyncThunk<
  TeamMember[],
  void,
  { rejectValue: { message: string } }
>(
  "companyMembers/fetchCompanyMembers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchCompanyMemberList()
      if (response.error) {
        return rejectWithValue({ message: response.error })
      }
      const membersData = response.data || []
      return membersData.map(transformMemberData)
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue({ message: error.message })
      }
      return rejectWithValue({
        message: "Unable to fetch company members. Please try again.",
      })
    }
  }
)

const companyMembersSlice = createSlice({
  name: "companyMembers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyMembers.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchCompanyMembers.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.members = action.payload
        state.error = null
      })
      .addCase(fetchCompanyMembers.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload?.message || "Failed to fetch company members."
      })
  },
})

export default companyMembersSlice.reducer

